// src/pages/Login.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { login, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Shield,
  AlertCircle,
  User,
  Sparkles,
  Key,
  ArrowRight,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";

const Login: React.FC = () => {
  const [loginInput, setLoginInput] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isEmail, setIsEmail] = useState<boolean | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user, token } = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    if (isAuthenticated && user) {
      if (!user.email_verified_at) {
        navigate("/verify-email", { state: { email: user.email } });
      } else {
        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (token && user && !user.email_verified_at) {
      navigate("/verify-email", { state: { email: user.email } });
    }
  }, [token, user, navigate]);

  const validateInputType = (input: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input);
  };

  const handleLoginChange = (value: string) => {
    setLoginInput(value);
    if (value) {
      setIsEmail(validateInputType(value));
    } else {
      setIsEmail(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginInput || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const result = await dispatch(
        login({
          login: loginInput,
          password,
        }) as any,
      );

      if (login.fulfilled.match(result)) {
        const response = result.payload;

        if (response.requires_verification) {
          toast.success("Verification code sent to your email!");
          navigate("/verify-email", {
            state: {
              email: validateInputType(loginInput)
                ? loginInput
                : response.user.email,
            },
          });
        } else {
          toast.success("Login successful!");
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img src={logo} alt="Pelivor Recovery" className="h-12 w-auto" />
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-emerald-400 rounded-full animate-pulse border-2 border-white dark:border-gray-900"></div>
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Welcome Back
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Sign in to your secure account
            </p>
          </div>
        </div>

        {/* Login Card */}
        <div className={`${GlobalStyles.card} !p-8`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email/Username Input */}
            <div className="space-y-2">
              <label
                htmlFor="login"
                className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
              >
                <User className="h-4 w-4 text-primary" />
                <span>Email or Username</span>
              </label>
              <div className="relative">
                <input
                  id="login"
                  name="login"
                  type="text"
                  autoComplete="username"
                  required
                  value={loginInput}
                  onChange={(e) => handleLoginChange(e.target.value)}
                  className={GlobalStyles.input}
                  placeholder="Enter email or username"
                  disabled={isLoading}
                />
                {loginInput && isEmail !== null && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                      {isEmail ? "Email" : "Username"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
                >
                  <Lock className="h-4 w-4 text-primary" />
                  <span>Password</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={GlobalStyles.input}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} w-full group/login`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                    ></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <span>Sign In</span>
                    <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover/login:translate-x-1" />
                  </div>
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                Don't have an account?
              </span>
            </div>
          </div>

          {/* Register Link */}
          <Link
            to="/register"
            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full group/register`}
          >
            <span>Create Account</span>
            <Sparkles className="h-4 w-4 ml-2 opacity-0 group-hover/register:opacity-100 transition-opacity" />
          </Link>

          {/* Security Info */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Shield className="h-4 w-4 text-emerald-500" />
              <span>Bank-level security & encryption</span>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 flex justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/terms" className="hover:text-primary transition-colors">
            Terms
          </Link>
          <span>•</span>
          <Link to="/privacy" className="hover:text-primary transition-colors">
            Privacy
          </Link>
          <span>•</span>
          <Link to="/about" className="hover:text-primary transition-colors">
            About
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
